<template>
  <div
    v-if="isLoading"
    class="p-20 flex justify-center"
  >
    <BaseSpinner />
  </div>
  <section v-else-if="useRequirementLevels && requirementLevels.length > 0">
    <template>
      <ExpandableController
        :expanded="expanded"
        @toggle="expanded = $event"
      >
        <template #default="{ expandables, trackToggle }">
          <Expandable
            v-for="(requirementLevel, requirementLevelIndex) in requirementLevels"
            :key="`level-${requirementLevelIndex}`"
            :expandable-id="`level-${requirementLevelIndex}`"
            :expanded="`level-${requirementLevelIndex}` in expandables ? expandables[`level-${requirementLevelIndex}`] : expanded"
            class="mb-3 rounded border-b pb-3"
            @toggle="trackToggle({ expandableId: `level-${requirementLevelIndex}`, expandableStatus: $event })"
          >
            <template #expandable-title>
              <div class="text-lg mb-2">{{ requirementLevel.label }}</div>
            </template>
            <template #expandable-content="{ expanded: expandedLevel }">
              <RichContent
                v-if="requirementLevel.config && requirementLevel.config.editorEnabled === true"
                class="reset-liststyle py-2 px-7 text-base md:text-lg"
                :content="getRequirementLevelNotes(requirementLevel.key)"
              />
              <div
                v-else
                class="mt-2"
              />
              <ExpandableController :expanded="expandedLevel">
                <template #expand-button><span /></template>
                <template #default="{ expandables:expandablesRequisite, trackToggle:trackToggleRequisite }">
                  <div
                    v-for="(requisite, index) in getRequirementLevels(requirementLevel.key)"
                    :key="index"
                  >
                    <template v-if="isNotNarrativeText(requisite.type)">
                      <Expandable
                        class="mb-2 bg-gray-200 rounded mx-7"
                        :expandable-id="`req-${index}`"
                        :expanded="`req-${index}` in expandablesRequisite ? expandablesRequisite[`req-${index}`] : expandedLevel"
                        @toggle="trackToggleRequisite({ expandableId: `req-${index}`, expandableStatus: $event })"
                      >
                        <template #expandable-title>
                          <div class="p-2">
                            {{ requisite.name || 'Untitled Requisite' }}
                            {{ getRequisitesCreditsRangeLabel(relatedRequisites, requisite.rules) }}
                          </div>
                        </template>
                        <template #expandable-content>
                          <div class="px-4 py-2 bg-white border-l border-r border-b rounded">
                            <div>
                              <strong>Type:</strong>
                              {{ decamelize(requisite.type) }}
                            </div>
                            <div
                              v-for="(rule, ruleIndex) in requisite.rules || []"
                              :key="`rule-${ruleIndex}`"
                            >
                              <RequisiteSimpleRule
                                class="mt-2 mb-4"
                                :rule="rule"
                                :related-requisites="relatedRequisites"
                                @showModal="$emit('showModal', $event)"
                              />
                            </div>
                            <div
                              v-if="isNoteNotEmpty(requisite)"
                              class="mt-2"
                            >
                              <strong>Additional Comments:</strong>
                              <RichContent
                                class="reset-liststyle"
                                :content="requisite.notes"
                              />
                            </div>
                          </div>
                        </template>
                      </Expandable>
                    </template>
                    <div v-else-if="isNoteNotEmpty(requisite)">
                      <RichContent
                        class="py-2"
                        :content="requisite.notes"
                      />
                    </div>
                  </div>
                </template>
              </ExpandableController>
              <div
                v-if="showRequisitesCredits"
                class="text-gray-400"
              >
                <BaseIcon
                  icon="info-circle"
                  class="small-info-icon"
                />
                <span>{{ totalRequisitesCredits(getRequirementLevels(requirementLevel.key)) }}</span>
              </div>
            </template>
          </Expandable>
          <Expandable
            class="mb-3 rounded border-b pb-3"
            expandable-id="no-levels"
            :expanded="'no-levels' in expandables ? expandables['no-levels'] : expanded"
            @toggle="trackToggle({ expandableId: 'no-levels', expandableStatus: $event })"
          >
            <template #expandable-title>
              <div class="text-lg mb-2">(No Requirement Levels)</div>
            </template>
            <template #expandable-content="{ expanded: expandedLevel }">
              <div class="mt-2" />
              <ExpandableController :expanded="expandedLevel">
                <template #expand-button><span /></template>
                <template #default="{ expandables:expandablesRequisite, trackToggle:trackToggleRequisite }">
                  <div
                    v-for="(requisite, index) in requirementsWithoutLevels"
                    :key="`no-level-${index}`"
                  >
                    <template v-if="isNotNarrativeText(requisite.type)">
                      <Expandable
                        class="mb-2 bg-gray-200 rounded mx-7"
                        :expandable-id="`req-${index}`"
                        :expanded="`req-${index}` in expandablesRequisite ? expandablesRequisite[`req-${index}`] : expandedLevel"
                        @toggle="trackToggleRequisite({ expandableId: `req-${index}`, expandableStatus: $event })"
                      >
                        <template #expandable-title>
                          <div class="p-2">
                            {{ requisite.name || 'Untitled Requisite' }}
                            {{ getRequisitesCreditsRangeLabel(relatedRequisites, requisite.rules) }}
                          </div>
                        </template>
                        <template #expandable-content>
                          <div class="px-4 py-2 bg-white border-l border-r border-b rounded">
                            <div>
                              <strong>Type:</strong>
                              {{ decamelize(requisite.type) }}
                            </div>
                            <div
                              v-for="(rule, ruleIndex) in requisite.rules || []"
                              :key="`rule-${ruleIndex}`"
                            >
                              <RequisiteSimpleRule
                                class="mt-2 mb-4"
                                :rule="rule"
                                :related-requisites="relatedRequisites"
                                @showModal="$emit('showModal', $event)"
                              />
                            </div>
                            <div
                              v-if="isNoteNotEmpty(requisite)"
                              class="mt-2"
                            >
                              <strong>Additional Comments:</strong>
                              <RichContent
                                class="reset-liststyle"
                                :content="requisite.notes"
                              />
                            </div>
                          </div>
                        </template>
                      </Expandable>
                    </template>
                    <div v-else-if="isNoteNotEmpty(requisite)">
                      <RichContent
                        class="py-2"
                        :content="requisite.notes"
                      />
                    </div>
                  </div>
                </template>
              </ExpandableController>
              <div
                v-if="showRequisitesCredits"
                class="text-gray-400"
              >
                <BaseIcon
                  icon="info-circle"
                  class="small-info-icon"
                />
                <span>{{ totalRequisitesCredits(requirementsWithoutLevels) }}</span>
              </div>
            </template>
          </Expandable>
        </template>
      </ExpandableController>
    </template>
  </section>
  <section v-else>
    <div>
      <ExpandableController
        :expanded="expanded"
        @toggle="expanded = $event"
      >
        <template #default="{ expandables, trackToggle }">
          <div
            v-for="(requisite, index) in sortedValue"
            :key="index"
          >
            <template v-if="isNotNarrativeText(requisite.type)">
              <Expandable
                class="mb-2 bg-gray-200 rounded"
                :expandable-id="index"
                :expanded="index in expandables ? expandables[index] : expanded"
                @toggle="trackToggle({ expandableId: index, expandableStatus: $event })"
              >
                <template #expandable-title>
                  <div class="p-2">
                    {{ requisite.name || 'Untitled Requisite' }}
                    {{ getRequisitesCreditsRangeLabel(relatedRequisites, requisite.rules) }}
                  </div>
                </template>
                <template #expandable-content>
                  <div class="px-4 py-2 bg-white border-l border-r border-b rounded">
                    <div>
                      <strong>Type:</strong>
                      {{ decamelize(requisite.type) }}
                    </div>
                    <div
                      v-for="(rule, ruleIndex) in requisite.rules || []"
                      :key="`rule-${ruleIndex}`"
                    >
                      <RequisiteSimpleRule
                        class="mt-2 mb-4"
                        :rule="rule"
                        :related-requisites="relatedRequisites"
                        @showModal="$emit('showModal', $event)"
                      />
                    </div>
                    <div
                      v-if="isNoteNotEmpty(requisite)"
                      class="mt-2"
                    >
                      <strong>Additional Comments:</strong>
                      <RichContent
                        class="reset-liststyle"
                        :content="requisite.notes"
                      />
                    </div>
                  </div>
                </template>
              </Expandable>
            </template>
            <div v-else-if="isNoteNotEmpty(requisite)">
              <RichContent
                class="py-2 text-base md:text-lg"
                :content="requisite.notes"
              />
            </div>
          </div>
        </template>
      </ExpandableController>
      <div
        v-if="showRequisitesCredits"
        class="text-gray-400"
      >
        <BaseIcon
          icon="info-circle"
          class="small-info-icon"
        />
        <span data-test="totalRequisitesCredits">{{ totalRequisitesCredits(sortedValue) }}</span>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import _get from 'lodash/get'
import _sortBy from 'lodash/sortBy'
import useRequisites from '../../useRequisites'
import RequisiteSimpleRule from './RequisiteSimpleRule'
import { decamelize, capitalize } from '@/utils/generalUtils'
import RichContent from '@/components/RichContent'
import useFeatureFlags from '@/composables/useFeatureFlags'
import {
  CA_REQUIREMENT_LEVELS,
  SHOW_REQUISITES_SUMMED_CREDITS,
  EXPAND_ALL_DM_REQUISITE,
} from '@/constants/featureFlags'
import Expandable from '@/components/Expandable'
import ExpandableController from '@/components/ExpandableController'
import { useContext } from '@nuxtjs/composition-api'
export default {
  components: {
    RequisiteSimpleRule,
    RichContent,
    Expandable,
    ExpandableController,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    documentValue: {
      type: Object,
      default: () => ({}),
    },
    entityType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      relatedRequisites: {
        courses: {},
        programs: {},
        courseSets: {},
        requisiteSets: {},
      },
      isLoading: false,
      requirementLevels: [],
    }
  },
  setup(props, { root }) {
    const { useRequirementLevels, showRequisitesCredits, expandAllDMRequisite } = useFeatureFlags({
      useRequirementLevels: CA_REQUIREMENT_LEVELS,
      showRequisitesCredits: SHOW_REQUISITES_SUMMED_CREDITS,
      expandAllDMRequisite: EXPAND_ALL_DM_REQUISITE,
    })
    const { $localize } = useContext()
    const {
      getRequisitesCreditsRange,
      getRequisitesCreditsRangeLabel,
      fetchRequisiteData,
    } = useRequisites({
      $axios: root.$axios,
      $store: root.$store,
      $localize,
    })

    return {
      useRequirementLevels,
      getRequisitesCreditsRange,
      getRequisitesCreditsRangeLabel,
      fetchRequisiteData,
      showRequisitesCredits,
      expanded: expandAllDMRequisite,
    }
  },
  computed: {
    ...mapState('settings', ['school']),
    requirementsWithoutLevels() {
      return this.sortedValue.filter(
        (v) =>
          this.requirementLevels
            .map((l) => l.key)
            .indexOf(v.requirementLevel) === -1
      )
    },
    sortedValue() {
      return _sortBy(this.value || [], 'position')
    },
    documentRequirementLevels() {
      return this.documentValue.requirementLevels || []
    },
    isProgram() {
      return this.entityType === 'program'
    },
  },
  async created() {
    this.isLoading = true
    if (this.useRequirementLevels) {
      const questions = await this.$axios.$get(
        `/${this.school}/general/${
          this.isProgram ? 'program' : 'course'
        }Template/questions`
      )
      if (questions) {
        const requirementLevels = _get(
          questions,
          'requisites.config.fields.requisitesSimple.config.fields.requirementLevels.config.options',
          []
        )
        this.requirementLevels = requirementLevels
      }
    }
    this.relatedRequisites = await this.fetchRequisiteData(this.value)
    this.isLoading = false
  },
  methods: {
    decamelize,
    getRequirementLevels(key) {
      return this.sortedValue.filter((v) => v.requirementLevel === key)
    },
    getRequirementLevelNotes(key) {
      const requirementLevel = this.documentRequirementLevels.find(
        (d) => d.key === key
      )
      if (requirementLevel) {
        return requirementLevel.notes || ''
      }
      return ''
    },
    isNoteNotEmpty(requisite) {
      return requisite.notes && requisite.notes !== '<p></p>'
    },
    isNotNarrativeText(type) {
      return type !== 'Narrative Text'
    },
    totalRequisitesCredits(requisites) {
      let requirementsCount = 0
      const entityName = this.isProgram ? 'Program' : 'Course'
      const { min, max } = requisites.reduce(
        (acc, requisite) => {
          requirementsCount++
          const { min, max } = this.getRequisitesCreditsRange(
            this.relatedRequisites,
            requisite.rules
          )

          acc.min += min
          acc.max += max

          return acc
        },
        {
          min: 0,
          max: 0,
        }
      )

      return `${requirementsCount} ${entityName} Requirements Totaling ${min} - ${max} ${capitalize(this.$localize('credit', 'plural'))}`
    },
  },
}
</script>

<style scoped>
.small-info-icon {
  height: 0.85rem;
  width: 0.85rem;
}
</style>
